import type { DynamicPlan } from '@/mocks/superAdminPlans';
import { formatStorage, formatPlanValue } from '@/mocks/superAdminPlans';

interface PlanCardProps {
  plan: DynamicPlan;
  assignedCount: number;
  onEdit: (plan: DynamicPlan) => void;
  onDuplicate: (plan: DynamicPlan) => void;
  onToggleActive: (plan: DynamicPlan) => void;
  onToggleVisible: (plan: DynamicPlan) => void;
  onDelete: (plan: DynamicPlan) => void;
}

export default function PlanCard({
  plan,
  assignedCount,
  onEdit,
  onDuplicate,
  onToggleActive,
  onToggleVisible,
  onDelete,
}: PlanCardProps) {
  const priceStr = plan.priceMonthly === 0 ? 'Gratis' : `$${plan.priceMonthly}/mes`;
  const annualStr = plan.priceAnnual > 0 ? `$${plan.priceAnnual}/año` : null;

  return (
    <div
      className={`relative bg-background-50 border-2 rounded-2xl p-5 flex flex-col transition-all hover:border-background-300 ${
        plan.active ? 'border-background-200' : 'border-background-200 opacity-70'
      }`}
    >
      {/* Status badges */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5">
        {!plan.active && (
          <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-semibold flex items-center gap-1">
            <i className="ri-pause-circle-line text-[9px]"></i>
            Inactivo
          </span>
        )}
        {!plan.visible && (
          <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-[9px] font-semibold flex items-center gap-1">
            <i className="ri-eye-off-line text-[9px]"></i>
            Oculto
          </span>
        )}
      </div>

      {/* Header */}
      <div className="text-center mb-4">
        <div className="flex items-center justify-center mb-2">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold"
            style={{ backgroundColor: `${plan.color}14`, color: plan.color }}
          >
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: plan.color }}></span>
            {plan.name}
          </span>
        </div>
        <div className="text-foreground-950 font-bold text-2xl">{priceStr}</div>
        {plan.priceMonthly > 0 && <div className="text-foreground-400 text-[10px]">por radio / mes</div>}
        {annualStr && (
          <div className="text-foreground-400 text-[10px] mt-0.5">{annualStr}</div>
        )}
      </div>

      {/* Trial Badge */}
      {plan.trialDays > 0 && plan.priceMonthly > 0 && (
        <div className="mb-4 p-2.5 rounded-xl bg-accent-50 border border-accent-200 flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center shrink-0">
            <i className="ri-timer-line text-accent-600 text-sm"></i>
          </div>
          <div>
            <p className="text-accent-800 text-[10px] font-bold">{plan.trialDays} días gratis</p>
            <p className="text-accent-500 text-[9px]">Luego ${plan.priceMonthly}/mes</p>
          </div>
        </div>
      )}

      {/* Limits grid */}
      <div className="mb-4 p-3 rounded-xl bg-background-100 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-medium">Almacenamiento</span>
          <span className="text-xs font-bold font-mono" style={{ color: plan.color }}>
            {formatStorage(plan.storageGB)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-medium">Locutores</span>
          <span className="text-xs font-bold font-mono text-foreground-800">{formatPlanValue(plan.locutores)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-medium">Programas</span>
          <span className="text-xs font-bold font-mono text-foreground-800">{formatPlanValue(plan.programs)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-medium">Eventos</span>
          <span className="text-xs font-bold font-mono text-foreground-800">{formatPlanValue(plan.events)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-medium">Lives</span>
          <span className="text-xs font-bold font-mono text-foreground-800">{formatPlanValue(plan.lives)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-foreground-400 text-[10px] font-medium">Notificaciones</span>
          <span className="text-xs font-bold font-mono text-foreground-800">{formatPlanValue(plan.notifications)}</span>
        </div>
      </div>

      {/* Features */}
      <div className="mb-4 space-y-1.5">
        <p className="text-foreground-400 text-[10px] font-semibold mb-1">Features</p>
        {plan.frequencyPreferred && (
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-[10px] text-emerald-500"></i>
            <span className="text-foreground-600 text-[10px]">Frecuencia preferida</span>
          </div>
        )}
        {plan.verification && (
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-[10px] text-emerald-500"></i>
            <span className="text-foreground-600 text-[10px]">Verificación</span>
          </div>
        )}
        {plan.customDomain && (
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-[10px] text-emerald-500"></i>
            <span className="text-foreground-600 text-[10px]">Dominio propio</span>
          </div>
        )}
        {plan.apps && (
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-[10px] text-emerald-500"></i>
            <span className="text-foreground-600 text-[10px]">Apps</span>
          </div>
        )}
        {!plan.frequencyPreferred && !plan.verification && !plan.customDomain && !plan.apps && (
          <span className="text-foreground-300 text-[10px] italic">Sin features premium</span>
        )}
      </div>

      {/* Radio count */}
      <div className="flex items-center gap-1.5 text-foreground-400 text-[10px] mb-4">
        <i className="ri-user-line text-[9px]"></i>
        <span>
          <strong className="text-foreground-600">{assignedCount}</strong> radios en este plan
        </span>
      </div>

      {/* Metadata */}
      <div className="text-foreground-300 text-[9px] mb-4 space-y-0.5">
        <p>Creado: {plan.createdAt}</p>
        <p>Actualizado: {plan.updatedAt}</p>
      </div>

      {/* Actions */}
      <div className="border-t border-background-200 pt-4 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onEdit(plan)}
            className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-background-100 text-foreground-700 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line text-[10px]"></i>
            Editar
          </button>
          <button
            onClick={() => onDuplicate(plan)}
            className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-background-100 text-foreground-700 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line text-[10px]"></i>
            Duplicar
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => onToggleActive(plan)}
            className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              plan.active
                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
            }`}
          >
            <i className={`${plan.active ? 'ri-pause-circle-line' : 'ri-play-circle-line'} text-[10px]`}></i>
            {plan.active ? 'Desactivar' : 'Activar'}
          </button>
          <button
            onClick={() => onToggleVisible(plan)}
            className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              plan.visible
                ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                : 'bg-sky-50 text-sky-700 hover:bg-sky-100'
            }`}
          >
            <i className={`${plan.visible ? 'ri-eye-off-line' : 'ri-eye-line'} text-[10px]`}></i>
            {plan.visible ? 'Ocultar' : 'Publicar'}
          </button>
        </div>
        <button
          onClick={() => onDelete(plan)}
          className="w-full flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-red-50 text-red-600 text-[10px] font-semibold hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-delete-bin-line text-[10px]"></i>
          Eliminar plan
        </button>
      </div>
    </div>
  );
}